import Avatar from '@mui/material/Avatar';
import type { ReactElement } from 'react';

/** Props of {@link ChannelAvatar}. */
export interface ChannelAvatarProps {
  /** Stable id that picks the colour, so a channel keeps its colour on every screen. */
  readonly seed: string;
  /** Channel name, source of the initials. */
  readonly name: string;
  /** Channel photo; the initials show while it loads or if it fails. */
  readonly photoUrl?: string | undefined;
  /** Diameter in px; 40 as in the channel lists of the mockups. */
  readonly size?: number | undefined;
}

/**
 * Initials of a channel name: first letter of the first two words ("Gold Signals Pro" → "GS"),
 * or the first two letters of a one-word name. Symbols and emoji are skipped.
 */
export function initialsOf(name: string): string {
  const words = name.match(/[\p{L}\p{N}]+/gu) ?? [];
  const [first = '', second] = words;
  const initials = second === undefined ? first.slice(0, 2) : `${first[0] ?? ''}${second[0] ?? ''}`;
  return initials.toLocaleUpperCase();
}

/** Index in `[0, count)` derived from `seed` with a small string hash (FNV-1a), stable across renders. */
export function colorIndex(seed: string, count: number): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % count;
}

/**
 * Round channel avatar: photo when available, else white initials on a colour taken from the
 * mockups' avatar set. Decorative (the channel name is always written next to it), so it is
 * hidden from assistive technologies.
 *
 * @example <ChannelAvatar seed={channel.subscriptionId} name={channel.channelName} />
 */
export function ChannelAvatar({
  seed,
  name,
  photoUrl,
  size = 40,
}: ChannelAvatarProps): ReactElement {
  return (
    <Avatar
      aria-hidden
      src={photoUrl}
      alt=""
      sx={(theme) => ({
        width: size,
        height: size,
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        bgcolor: theme.fx.avatars[colorIndex(seed, theme.fx.avatars.length)],
      })}
    >
      {initialsOf(name)}
    </Avatar>
  );
}
